import { jsPDF } from 'jspdf';
import MarkdownIt from 'markdown-it';

// Formatted markdown PDF. Walks markdown-it tokens to render headings, lists,
// blockquotes, fenced code, and inline emphasis (bold, italic, underline,
// strike, inline code) with jsPDF's `times`/`courier` fonts. Private hidden
// snippet bodies are stripped — only blocks with an explicit `visible=`
// audience survive (tags stripped, body preserved).

const mdFmt = MarkdownIt('default', { html: false, linkify: true, breaks: false });

// Strips `[[hidden id=X]]...[[/hidden]]` blocks that have no `visible=`
// audience (private to owner/admin) and redacted placeholders. Hidden blocks
// with any explicit `visible=` audience are kept — tags stripped, body
// preserved — so intentionally-shared snippets land in the export.
function stripHiddenSnippets(text) {
  if (!text) return '';
  return text.replace(
    /\[\[hidden\s+([^\]]+?)\]\](?:([\s\S]*?)\[\[\/hidden\]\])?/g,
    (_match, attrs, body) => {
      const a = String(attrs);
      if (/\bredacted\b/.test(a)) return '';
      const vis = /\bvisible=([^\s\]]*)/.exec(a);
      const audience = vis ? vis[1].split(',').map((s) => s.trim()).filter(Boolean) : [];
      if (audience.length === 0) return '';
      return body || '';
    },
  );
}

// Strips `<char>NAME</char>` inline tags used by screenplay format. Tags are
// stripped for display/export — only the name inside survives.
function stripCharTags(text) {
  if (!text) return '';
  return text.replace(/<char[^>]*?>\s*([^<]+?)\s*<\/char>/gi, '$1');
}

// Walk inline-token children into a flat list of styled runs that
// `drawRuns` can layout.
function inlineRuns(token, base = {}) {
  const out = [];
  const style = { ...base };
  let stack = [];
  const push = (text) => {
    if (!text) return;
    out.push({ text, ...style });
  };
  const apply = (k, v) => {
    stack.push({ k, prev: style[k] });
    style[k] = v;
  };
  const restore = () => {
    const { k, prev } = stack.pop() || {};
    if (k != null) style[k] = prev;
  };
  for (const c of token.children || []) {
    switch (c.type) {
      case 'text': push(c.content); break;
      case 'softbreak': push(' '); break;
      case 'hardbreak': push('\n'); break;
      case 'strong_open': apply('bold', true); break;
      case 'strong_close': restore(); break;
      case 'em_open': apply('italic', true); break;
      case 'em_close': restore(); break;
      case 's_open': apply('strike', true); break;
      case 's_close': restore(); break;
      case 'u_open': apply('underline', true); break;
      case 'u_close': restore(); break;
      case 'code_inline':
        out.push({ text: c.content, ...style, mono: true });
        break;
      case 'link_open': {
        const href = c.attrGet('href');
        apply('link', href);
        break;
      }
      case 'link_close': restore(); break;
      default: break;
    }
  }
  return out;
}

function setRunFont(doc, run, baseSize) {
  const fam = run.mono ? 'courier' : 'times';
  let style = 'normal';
  if (run.bold && run.italic) style = 'bolditalic';
  else if (run.bold) style = 'bold';
  else if (run.italic) style = 'italic';
  doc.setFont(fam, style).setFontSize(baseSize);
}

// Word-wrap a list of runs across `width` starting at `(x, y)`. Underlines
// drawn as a 1pt rule under each underlined run. Returns the next y.
function drawRuns(doc, runs, x, y, width, lineHeight, baseSize, ensureRoom) {
  let cx = x;
  let ly = y;
  const startNewLine = () => {
    cx = x;
    ly += lineHeight;
    const moved = ensureRoom(ly);
    if (moved != null) ly = moved;
  };
  for (const run of runs) {
    if (!run.text) continue;
    const segments = run.text.split('\n');
    for (let si = 0; si < segments.length; si++) {
      if (si > 0) startNewLine();
      const tokens = segments[si].split(/(\s+)/);
      for (const tok of tokens) {
        if (!tok) continue;
        setRunFont(doc, run, baseSize);
        const w = doc.getTextWidth(tok);
        if (/^\s+$/.test(tok)) {
          if (cx === x) continue;
          if (cx + w > x + width) startNewLine();
          else cx += w;
          continue;
        }
        if (cx + w > x + width && cx !== x) startNewLine();
        setRunFont(doc, run, baseSize);
        doc.text(tok, cx, ly);
        if (run.underline) {
          doc.setLineWidth(0.6);
          doc.line(cx, ly + 1.5, cx + w, ly + 1.5);
        }
        if (run.link) {
          doc.setTextColor(40, 80, 200);
          doc.text(tok, cx, ly);
          doc.setTextColor(0, 0, 0);
        }
        cx += w;
      }
    }
  }
  return ly + lineHeight;
}

function renderMarkdown(doc, source, ctx) {
  const tokens = mdFmt.parse(stripHiddenSnippets(source), {});
  const { x, width, bodySize, lineHeight, ensureRoom } = ctx;
  let y = ctx.y;
  let listStack = []; // [{ type: 'bullet'|'ordered', idx, indent }]

  const blockGap = lineHeight * 0.5;

  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i];
    switch (t.type) {
      case 'heading_open': {
        const level = +t.tag.slice(1);
        const size = level === 1 ? 22 : level === 2 ? 18 : 15;
        const lh = size + 6;
        y = ensureRoom(y) ?? y;
        const inline = tokens[++i];
        const runs = inlineRuns(inline, { bold: true });
        y = drawRuns(doc, runs, x, y, width, lh, size, ensureRoom);
        i++; // heading_close
        y += blockGap;
        break;
      }
      case 'paragraph_open': {
        const inline = tokens[++i];
        const indent = listStack.length ? listStack[listStack.length - 1].indent : 0;
        const runs = inlineRuns(inline);
        const px = x + indent;
        const pw = width - indent;
        y = drawRuns(doc, runs, px, y, pw, lineHeight, bodySize, ensureRoom);
        i++; // paragraph_close
        if (!listStack.length) y += blockGap;
        break;
      }
      case 'bullet_list_open':
        listStack.push({ type: 'bullet', idx: 0, indent: (listStack.length + 1) * 18 });
        break;
      case 'ordered_list_open':
        listStack.push({ type: 'ordered', idx: +(t.attrGet('start') || 1), indent: (listStack.length + 1) * 18 });
        break;
      case 'bullet_list_close':
      case 'ordered_list_close':
        listStack.pop();
        y += blockGap;
        break;
      case 'list_item_open': {
        const top = listStack[listStack.length - 1];
        const marker = top.type === 'bullet' ? '• ' : `${top.idx}. `;
        if (top.type === 'ordered') top.idx += 1;
        doc.setFont('times', 'normal').setFontSize(bodySize);
        y = ensureRoom(y) ?? y;
        doc.text(marker, x + top.indent - 14, y);
        break;
      }
      case 'list_item_close':
        break;
      case 'blockquote_open': {
        // Render whole blockquote body together so we can draw the rule.
        const startY = y;
        // Walk inner tokens until matching close.
        let depth = 1;
        const inner = [];
        i++;
        while (i < tokens.length && depth > 0) {
          if (tokens[i].type === 'blockquote_open') depth++;
          if (tokens[i].type === 'blockquote_close') { depth--; if (depth === 0) break; }
          inner.push(tokens[i]);
          i++;
        }
        // Render inner as a mini block.
        const indent = 24;
        const innerCtx = { ...ctx, x: x + indent, width: width - indent, y };
        const newY = renderMarkdown(doc, '', { ...innerCtx, y });
        // Trick: feed tokens directly. Simpler — render runs of paragraphs.
        let by = startY;
        for (let j = 0; j < inner.length; j++) {
          const it = inner[j];
          if (it.type === 'paragraph_open') {
            const il = inner[++j];
            const runs = inlineRuns(il, { italic: true });
            by = drawRuns(doc, runs, x + indent, by, width - indent, lineHeight, bodySize, ensureRoom);
            j++; // close
            by += blockGap * 0.5;
          }
        }
        doc.setDrawColor(80);
        doc.setLineWidth(1.5);
        doc.line(x + 6, startY - lineHeight + 4, x + 6, by - lineHeight + 4);
        doc.setDrawColor(0);
        y = by + blockGap;
        void newY; // ignore — we used direct path above
        break;
      }
      case 'code_block':
      case 'fence': {
        const lang = (t.info || '').trim();
        const body = t.content.replace(/\n$/, '');
        const lines = body.split('\n');
        const codeSize = 10;
        const codeLh = 13;
        const pad = 8;
        const startY = y;
        y += pad;
        doc.setFont('courier', 'normal').setFontSize(codeSize);
        for (const ln of lines) {
          const wrapped = doc.splitTextToSize(ln || ' ', width - pad * 2);
          for (const seg of wrapped) {
            y = ensureRoom(y) ?? y;
            doc.text(seg, x + pad, y);
            y += codeLh;
          }
        }
        const endY = y + pad - codeLh;
        // Background box (drawn after, would overwrite — use stroke only).
        doc.setDrawColor(180);
        doc.setLineWidth(0.5);
        doc.rect(x, startY - codeLh + 4, width, endY - startY + codeLh);
        doc.setDrawColor(0);
        if (lang) {
          doc.setFont('courier', 'bold').setFontSize(8);
          doc.setTextColor(120);
          doc.text(lang.toUpperCase(), x + width - pad, startY - 2, { align: 'right' });
          doc.setTextColor(0);
        }
        y = endY + blockGap;
        break;
      }
      case 'hr': {
        y = ensureRoom(y) ?? y;
        doc.setDrawColor(160);
        doc.setLineWidth(0.6);
        doc.line(x, y - 4, x + width, y - 4);
        doc.setDrawColor(0);
        y += lineHeight;
        break;
      }
      default:
        break;
    }
  }
  return y;
}

// Builds a formatted PDF: cover + clickable table of contents + chapters
// rendered from markdown with headings, lists, code blocks, and inline
// emphasis. Hidden snippet bodies are stripped before render.
//
// Screenplay mode (`opts.codexType === 'screenplay'`) switches to Courier and
// renders line-prefixed cues (`@@CHR`, `@@DLG`, `@@PAR`, `@@TRN`) with Celtx-
// style indents, plus relabels TOC and chapter fallbacks as "Scenes".
export function buildRawTextPDF(title, chapters, opts = {}) {
  const isScreenplay = opts.codexType === 'screenplay';
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  const PAGE_W_LTR = 612;
  const PAGE_H_LTR = 792;
  const CM = 28.3465; // 1cm in PDF points
  // Master Scenes margins (screenplay) vs prose 1in margins.
  const MG = isScreenplay
    ? { left: 3.8 * CM, right: 2.5 * CM, top: 2.5 * CM, bottom: 2.5 * CM }
    : { left: 72, right: 72, top: 72, bottom: 72 };
  const BODY_W = PAGE_W_LTR - MG.left - MG.right;
  const BODY_FONT = 12;
  const BODY_LINE = isScreenplay ? 14.4 : 17; // 12pt single spacing ≈ 14.4
  const baseFont = isScreenplay ? 'courier' : 'times';
  const tocLabel = isScreenplay ? 'Scenes' : 'Contents';
  const chapterFallback = (i) => (isScreenplay ? `Scene ${i + 1}` : `Chapter ${i + 1}`);

  // --- Cover ---
  doc.setFont(baseFont, 'bold').setFontSize(28);
  doc.text((title || 'Untitled').toUpperCase(), PAGE_W_LTR / 2, PAGE_H_LTR / 2 - 20, {
    align: 'center',
  });
  doc.setFont(baseFont, 'italic').setFontSize(12);
  doc.text('written in Sabiá', PAGE_W_LTR / 2, PAGE_H_LTR / 2 + 12, { align: 'center' });

  // --- TOC (not for screenplay) ---
  const tocEntries = [];
  if (!isScreenplay) {
    doc.addPage();
    doc.setFont(baseFont, 'bold').setFontSize(20);
    doc.text(tocLabel, MG.left, MG.top);
    let tocY = MG.top + 36;
    doc.setFont(baseFont, 'normal').setFontSize(BODY_FONT);
    for (let i = 0; i < chapters.length; i++) {
      const ch = chapters[i];
      if (tocY > PAGE_H_LTR - MG.bottom) {
        doc.addPage();
        tocY = MG.top;
      }
      tocEntries.push({ y: tocY, page: doc.internal.getNumberOfPages(), title: ch.title || chapterFallback(i) });
      doc.text(`${i + 1}. ${ch.title || chapterFallback(i)}`, MG.left, tocY);
      tocY += BODY_LINE + 4;
    }
  }

  // --- Chapters ---
  const chapterStarts = [];
  const ensureRoom = (yRef) => {
    if (yRef > PAGE_H_LTR - MG.bottom) {
      doc.addPage();
      return MG.top;
    }
    return null;
  };

  let y = MG.top;
  for (let i = 0; i < chapters.length; i++) {
    const ch = chapters[i];
    const isLast = i === chapters.length - 1;
    // Screenplay scenes flow continuously — TRN is the only separator.
    // Prose chapters each start on a fresh page.
    if (!isScreenplay) {
      doc.addPage();
      y = MG.top;
    } else if (i === 0) {
      doc.addPage();
      y = MG.top;
    } else {
      y += BODY_LINE * 1.5;
      y = ensureRoom(y) ?? y;
    }
    chapterStarts.push(doc.internal.getNumberOfPages());

    doc.setFont(baseFont, isScreenplay ? 'normal' : 'bold').setFontSize(isScreenplay ? 14 : 22);
    const titleLines = doc.splitTextToSize(ch.title || chapterFallback(i), BODY_W);
    for (const line of titleLines) {
      y = ensureRoom(y) ?? y;
      doc.text(isScreenplay ? line.toUpperCase() : line, MG.left, y);
      y += isScreenplay ? 18 : 26;
    }
    y += 12;

    if (isScreenplay) {
      y = renderScreenplay(doc, ch.content || '', {
        x: MG.left,
        y,
        width: BODY_W,
        pageWidth: PAGE_W_LTR,
        margins: MG,
        cm: CM,
        lineHeight: BODY_LINE,
        bodySize: BODY_FONT,
        ensureRoom,
        isLast,
      });
    } else {
      y = renderMarkdown(doc, stripCharTags(ch.content || ''), {
        x: MG.left,
        y,
        width: BODY_W,
        bodySize: BODY_FONT,
        lineHeight: BODY_LINE,
        ensureRoom,
      });
    }
  }

  // --- Patch TOC page numbers + outline (general codices only) ---
  if (tocEntries.length > 0) {
    doc.setFont(baseFont, 'normal').setFontSize(BODY_FONT);
    for (let i = 0; i < tocEntries.length; i++) {
      const entry = tocEntries[i];
      const pageNum = chapterStarts[i];
      doc.setPage(entry.page);
      doc.text(String(pageNum), PAGE_W_LTR - MG.right, entry.y, { align: 'right' });
      doc.outline.add(null, entry.title, { pageNumber: pageNum });
    }
  }

  doc.save(`${(title || 'document').replace(/\s+/g, '_')}.pdf`);
}

// Renders screenplay body line-by-line per Master Scenes format (Courier 12pt,
// US Letter):
//   left margin 3.8cm, right 2.5cm, top/bottom 2.5cm
//   Scene heading (# foo): left margin, uppercase, bold
//   Character (@@CHR n):   absolute 10cm from page left, uppercase, bold
//   Dialog (@@DLG t):      7.5cm left indent, 5cm right margin, single-spaced
//   Parenthetical (@@PAR): 7.5cm indent, italic, wrap in parens
//   Action (plain):        left margin
//   Transition (@@TRN):    right-aligned at right margin, uppercase
function renderScreenplay(doc, source, ctx) {
  const { x, width, pageWidth, margins, cm, lineHeight, bodySize, ensureRoom, isLast } = ctx;
  const cleaned = stripCharTags(stripHiddenSnippets(source));
  const lines = cleaned.split(/\r?\n/);
  let y = ctx.y;

  // Master Scenes absolute offsets (from page-left), in PDF points.
  const X_CHAR = 10.0 * cm;             // character cue
  const X_DIA  = 7.5 * cm;              // dialog left
  const X_PAR  = 7.5 * cm;              // parenthetical
  const W_DIA  = pageWidth - X_DIA - 5.0 * cm; // dialog block right margin 5cm
  const W_PAR  = pageWidth - X_PAR - 5.0 * cm;
  const W_CHR  = pageWidth - X_CHAR - margins.right;

  const draw = (text, opts) => {
    if (!text && !opts?.allowEmpty) {
      y += lineHeight * 0.5;
      return;
    }
    const {
      font = 'normal',
      size = bodySize,
      absX = null,
      align = 'left',
      maxW = width,
      tight = false,
    } = opts || {};
    doc.setFont('courier', font).setFontSize(size);
    const wrapped = doc.splitTextToSize(text, maxW);
    const lh = tight ? lineHeight : lineHeight;
    for (const seg of wrapped) {
      y = ensureRoom(y) ?? y;
      if (align === 'right') {
        doc.text(seg, pageWidth - margins.right, y, { align: 'right' });
      } else {
        doc.text(seg, absX != null ? absX : x, y);
      }
      y += lh;
    }
  };

  // Track whether the scene already ends with an explicit transition so we
  // can auto-emit `CUT TO:` when the writer forgot to define one.
  let lastWasTrn = false;
  let hadContent = false;

  for (const raw of lines) {
    const line = raw.replace(/\s+$/g, '');
    if (!line.trim()) {
      y += lineHeight * 0.6;
      continue;
    }

    // Skip orphan markers (e.g. `@@CHR` with no body — happens when writer
    // inserts the tag but never types a name).
    if (/^@@(SCN|CHR|DLG|PAR|TRN)\s*$/i.test(line.trim())) continue;

    const scn = line.match(/^@@SCN\s+(.+)$/i);
    if (scn) {
      const heading = scn[1].trim().toUpperCase();
      y += lineHeight * 0.6;
      draw(heading, { font: 'bold' });
      y += lineHeight * 0.4;
      hadContent = true;
      lastWasTrn = false;
      continue;
    }

    if (/^#\s+/.test(line)) {
      const heading = line.replace(/^#+\s+/, '').toUpperCase();
      y += lineHeight * 0.6;
      draw(heading, { font: 'bold' });
      y += lineHeight * 0.4;
      hadContent = true;
      lastWasTrn = false;
      continue;
    }

    // Character cue alone on its own line: either legacy `@@CHR NAME` or the
    // new inline wrapper `<char>NAME</char>` (when the whole paragraph is just
    // the wrapper, the editor decorates it as a full character cue too).
    const aloneChar = line.trim().match(/^<char>\s*([^<]+?)\s*<\/char>$/i);
    if (aloneChar) {
      y += lineHeight * 0.6;
      draw(aloneChar[1].trim().toUpperCase(), {
        font: 'bold',
        absX: X_CHAR,
        maxW: W_CHR,
        tight: true,
      });
      hadContent = true;
      lastWasTrn = false;
      continue;
    }

    const chr = line.match(/^@@CHR\s+(.+)$/i);
    if (chr) {
      y += lineHeight * 0.6;
      draw(chr[1].trim().toUpperCase(), {
        font: 'bold',
        absX: X_CHAR,
        maxW: W_CHR,
        tight: true,
      });
      hadContent = true;
      lastWasTrn = false;
      continue;
    }

    const par = line.match(/^@@PAR\s+(.+)$/i);
    if (par) {
      let body = par[1].trim();
      if (!/^\(.*\)$/.test(body)) body = `(${body})`;
      draw(body, {
        font: 'italic',
        absX: X_PAR,
        maxW: W_PAR,
        tight: true,
      });
      hadContent = true;
      lastWasTrn = false;
      continue;
    }

    const dlg = line.match(/^@@DLG\s+(.+)$/i);
    if (dlg) {
      // Inline character mentions inside dialog body render as UPPERCASE name.
      const body = dlg[1].trim().replace(/<char>\s*([^<]+?)\s*<\/char>/gi, (_, n) => n.trim().toUpperCase());
      draw(body, {
        absX: X_DIA,
        maxW: W_DIA,
        tight: true,
      });
      y += lineHeight * 0.2;
      hadContent = true;
      lastWasTrn = false;
      continue;
    }

    const trn = line.match(/^@@TRN\s+(.+)$/i);
    if (trn) {
      // Last scene must not show any transition — drop it even if writer
      // left one in the body.
      if (isLast) continue;
      y += lineHeight * 0.6;
      draw(trn[1].trim().toUpperCase(), { font: 'bold', align: 'right' });
      y += lineHeight * 0.4;
      hadContent = true;
      lastWasTrn = true;
      continue;
    }

    // Action / scene description — plain courier at left margin.
    // Strip any embedded screenplay markers that leaked into the line, and
    // replace inline `<char>NAME</char>` mentions with uppercase NAME.
    const plain = line
      .replace(/<char>\s*([^<]+?)\s*<\/char>/gi, (_, n) => n.trim().toUpperCase())
      .replace(/@@(SCN|CHR|DLG|PAR|TRN)\b\s*/gi, '')
      .replace(/[*_`~]/g, '');
    if (!plain.trim()) continue;
    draw(plain);
    hadContent = true;
    lastWasTrn = false;
  }

  // Auto-append `CUT TO:` if the scene has content but no closing transition.
  // Suppressed on the last scene — final scene must end clean.
  if (hadContent && !lastWasTrn && !isLast) {
    y += lineHeight * 0.6;
    doc.setFont('courier', 'bold').setFontSize(bodySize);
    y = ensureRoom(y) ?? y;
    doc.text('CUT TO:', pageWidth - margins.right, y, { align: 'right' });
    y += lineHeight;
  }

  return y;
}
